/**
 * Deep Research Language Mirror
 *
 * Self-contained, dependency-free helpers that detect the user's input
 * language and build a stage-prefix directive that pins all user-facing
 * output (Stage 1 initial findings + clarifying questions, Stage 2 brief,
 * Stage 3 final report) to that same language.
 *
 * Background (Miles + Malaika QA pass, 2026-05-11): the deterministic
 * pre-planner trigger from PR #128 correctly fires the deep-research
 * workflow on Spanish verbs (`investigación profunda`, `investigar a
 * fondo`, etc.), but every stage's system prompt was hard-coded
 * English-locked. A Spanish-speaking user got an English `initial
 * findings:` block, English clarifying questions, and an English
 * Stage-3 brief. That was a real legal-product regression for the
 * firm's primary Spanish-speaking client base.
 *
 * Lives outside `handlers.ts` so it can be unit-tested via `ts-node`
 * without dragging React / Firebase transitive imports.
 */

export type DetectedLanguage = 'en' | 'es';

export function detectInputLanguage(text: string): DetectedLanguage {
  if (!text || text.trim().length < 3) return 'en';
  const sample = text.toLowerCase();
  const spanishAccents = /[áéíóúüñ¿¡]/;
  const spanishWords =
    /\b(el|la|los|las|un|una|de|del|y|en|que|es|por|con|para|como|su|se|no|mi|me|te|le|nos|les|si|yo|tu|tengo|tiene|quiero|necesito|puedo|puede|soy|eres|hay|estoy|está|somos|estamos|están|gracias|hola|buenos|días|tardes|favor|cómo|cuándo|dónde|qué|quién|también|pero|porque|muy|más|bien|mucho|asilo|inmigración|abogado|caso|investigación|investigar|profunda|profundamente|fondo|jurisprudencia|ley|leyes|sobre|acerca|laboral|compensación|audiencia|calendario|maestro)\b/gi;
  let score = 0;
  if (spanishAccents.test(sample)) score += 3;
  const matches = sample.match(spanishWords);
  if (matches) score += matches.length;
  return score >= 2 ? 'es' : 'en';
}

export function buildLanguageDirective(language: DetectedLanguage): string {
  if (language === 'es') {
    return [
      'ABSOLUTE LANGUAGE RULE — READ FIRST, OVERRIDES EVERY EXAMPLE BELOW:',
      '',
      'El usuario hizo su consulta en ESPAÑOL. Toda salida orientada al usuario que generes en esta etapa DEBE estar redactada íntegramente en español, incluyendo:',
      '- Los hallazgos preliminares / "hallazgos iniciales" (Stage 1 initial findings)',
      '- Los encabezados, subtítulos y texto explicativo ("hallazgos iniciales", "cómo te ayudarán tus respuestas", etc.)',
      '- Las preguntas aclaratorias dirigidas al usuario',
      '- El brief de Stage 2 y el reporte final de Stage 3 (tesis, resumen ejecutivo, análisis de los tres niveles, conclusión, recomendaciones, etc.)',
      '- Los títulos de documento y las etiquetas de sección (Resumen Ejecutivo, Tesis, Análisis Legal, Marco Legal, Contexto, Implementación Estratégica, Referencias, etc.)',
      '',
      'PRESERVA EN SU IDIOMA ORIGINAL (no traduzcas):',
      '- Citas legales y nombres de autoridades (p. ej. "INA § 240(a)", "8 C.F.R. § 1208.16", "Cal. Lab. Code § 5402(b)", "Matter of L-A-C-, 26 I&N Dec. 516", "Yanowitz v. L\'Oreal USA")',
      '- Nombres de formularios y agencias (p. ej. "Form I-589", "USCIS", "EOIR", "WCAB", "DWC-1", "AB 5")',
      '- Códigos legales en su forma canónica',
      '',
      'El idioma del usuario es la regla principal. Si esta regla entra en conflicto con cualquier ejemplo en inglés más abajo, esta regla GANA.',
      '',
      '',
    ].join('\n');
  }
  return [
    'ABSOLUTE LANGUAGE RULE — READ FIRST:',
    '',
    "The user's query was in English. All user-facing output you generate in this stage MUST be in English. Code citations and statute names stay in their canonical form.",
    '',
    '',
  ].join('\n');
}

// ANSWER-FIRST REDESIGN (owner directive 2026-08-20, supersedes 2026-08-19
// findings-first): the clarify card leads with the ANSWER (initial findings
// = the summarized answer), strategically offers the fully cited report, and
// tells the user to answer below in chat only when questions exist. The
// "how your answers will help" chrome is removed entirely. The OLD header phrases ("a few
// questions to focus your research" / "unas preguntas para enfocar tu
// investigación") remain recognized by every history matcher (refinement
// guard, planner shape rules) for cards already in threads — never remove
// them from those matchers.
export const DEEP_RESEARCH_CHROME_STRINGS = {
  en: {
    qualificationHeader: 'initial findings — a few quick questions',
    questionsTitle: 'to help us focus the research, please consider',
    offerTitle: 'want the full deep research report?',
    offerBody:
      'fully cited and source-verified, delivered as PDF + DOCX — your quick answers below make it sharper and more strategic.',
    helperText:
      'answer any of the questions below in chat — or run the report now.',
    skipButton: 'skip to deep research',
    videoScriptButton: 'write video script',
    initialFindingsLabel: 'initial findings:',
  },
  es: {
    qualificationHeader: 'hallazgos iniciales — unas preguntas rápidas',
    questionsTitle:
      'para enfocar mejor la investigación, por favor considera',
    offerTitle: '¿quieres el informe completo de investigación a fondo?',
    offerBody:
      'totalmente citado y con fuentes verificadas, entregado como PDF + DOCX — tus respuestas rápidas abajo lo hacen más preciso y estratégico.',
    helperText:
      'responde cualquiera de las preguntas abajo en el chat — o genera el informe ahora.',
    skipButton: 'ir directo a la investigación',
    videoScriptButton: 'escribir guión de video',
    initialFindingsLabel: 'hallazgos iniciales:',
  },
} as const;

export type DeepResearchChromeStrings = {
  qualificationHeader: string;
  questionsTitle: string;
  offerTitle: string;
  offerBody: string;
  helperText: string;
  skipButton: string;
  videoScriptButton: string;
  initialFindingsLabel: string;
};

export function getDeepResearchChromeStrings(
  language: DetectedLanguage,
): DeepResearchChromeStrings {
  return DEEP_RESEARCH_CHROME_STRINGS[language];
}
